import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Mail, ExternalLink, LogOut, ArrowLeft } from "lucide-react";
import { useNoIndex } from "../lib/seoHelpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY_COLOR = {
  critical: "text-red-400 bg-red-500/15",
  high: "text-orange-400 bg-orange-500/15",
  "medium-high": "text-yellow-400 bg-yellow-500/15",
  medium: "text-blue-400 bg-blue-500/15",
  "low-medium": "text-cyan-400 bg-cyan-500/15",
  unknown: "text-slate-400 bg-slate-500/15",
};

export default function AdminTriagePage() {
  useNoIndex();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("sdc_admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sdc_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }

    const fetchAll = async () => {
      try {
        const res = await axios.get(`${API}/admin/triage`, { headers: getAuthHeaders() });
        setItems(res.data || []);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          localStorage.removeItem("sdc_admin_token");
          localStorage.removeItem("sdc_admin_user");
          navigate("/admin");
        } else {
          setError("Couldn't load triage submissions. Check backend logs.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [navigate, getAuthHeaders]);

  const logout = () => {
    localStorage.removeItem("sdc_admin_token");
    localStorage.removeItem("sdc_admin_user");
    navigate("/admin");
  };

  // Derived aggregates for the top stat strip
  const totalSubmissions = items.length;
  const withEmail = items.filter((i) => i.email).length;
  const recent24h = items.filter((i) => {
    const t = new Date(i.createdAt);
    return Date.now() - t.getTime() < 24 * 60 * 60 * 1000;
  }).length;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-white/10 bg-slate-900/40 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
              Cases
            </Link>
            <span className="text-slate-700">·</span>
            <h1 className="text-lg font-semibold">Suspension Analyzer Leads</h1>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"
            data-testid="admin-triage-logout"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        {/* Stat strip */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="rounded-xl border border-white/10 bg-white/5 p-5" data-testid="triage-stat-total">
            <p className="text-xs uppercase tracking-wider text-slate-500">Total submissions</p>
            <p className="text-3xl font-bold mt-1">{totalSubmissions}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-5" data-testid="triage-stat-email">
            <p className="text-xs uppercase tracking-wider text-slate-500">With email captured</p>
            <p className="text-3xl font-bold mt-1">{withEmail}</p>
            <p className="text-xs text-slate-400 mt-1">
              {totalSubmissions > 0 ? Math.round((withEmail / totalSubmissions) * 100) : 0}% capture rate
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-5" data-testid="triage-stat-recent">
            <p className="text-xs uppercase tracking-wider text-slate-500">Last 24h</p>
            <p className="text-3xl font-bold mt-1">{recent24h}</p>
          </div>
        </div>

        {/* Submissions list */}
        {loading ? (
          <div className="flex items-center gap-3 text-slate-400 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading…
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 text-red-300" data-testid="triage-error">
            {error}
          </div>
        ) : totalSubmissions === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-10 text-center" data-testid="triage-empty">
            <h2 className="text-xl font-semibold mb-2">No triage submissions yet</h2>
            <p className="text-slate-400 text-sm mb-4">
              When visitors complete the free Suspension Analyzer at <code>/analyze</code>, their submissions will appear here.
            </p>
            <Link
              to="/analyze"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:underline"
            >
              View the analyzer
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">When</th>
                  <th className="text-left px-5 py-3">Platform</th>
                  <th className="text-left px-5 py-3">Category</th>
                  <th className="text-left px-5 py-3">Severity</th>
                  <th className="text-left px-5 py-3">Account</th>
                  <th className="text-left px-5 py-3">Tier</th>
                  <th className="text-left px-5 py-3">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {items.map((row) => {
                  const sev = SEVERITY_COLOR[row.classifiedSeverity] || SEVERITY_COLOR.unknown;
                  return (
                    <tr key={row.id} className="hover:bg-white/5" data-testid={`triage-row-${row.id}`}>
                      <td className="px-5 py-3 text-slate-400">
                        {new Date(row.createdAt).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-5 py-3 capitalize">{row.platform}</td>
                      <td className="px-5 py-3 text-slate-300">{row.classifiedCategory.replace(/_/g, " ")}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${sev}`}>
                          {row.classifiedSeverity}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400 text-xs">{row.accountType}</td>
                      <td className="px-5 py-3 text-blue-400 text-xs">{row.recommendedTier}</td>
                      <td className="px-5 py-3">
                        {row.email ? (
                          <a
                            href={`mailto:${row.email}`}
                            className="inline-flex items-center gap-1.5 text-blue-400 hover:underline text-xs"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            {row.email}
                          </a>
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
